import { createApp } from 'vue'
import 'vue3-perfect-scrollbar/style.css'
import * as rules from '@vee-validate/rules'
import { configure, defineRule } from 'vee-validate'
import { PerfectScrollbarPlugin } from 'vue3-perfect-scrollbar'

import { hasFeature } from '@/mixins'
import VueSelect from "vue-select"
import { clickOutside } from '@/directives'
import installCustomRules from '@/validation/rules'
import { i18n, loadLanguageAsync } from '@/translations/i18n'
import { snakeToPascal, cleanSource, toggleModal, showSnackbar, toggleBodyClass, toggleHtmlClass } from '@/helper'

import Checkout from '@/app/Checkout/index.vue'
import QuickCart from '@/app/QuickCart/index.vue'
import BaseModal from '@/components/BaseModal/index.vue'
import { createOverlayRouter } from '@/app/Checkout/router'

import $ from 'jquery'
import store from '@/store'
import 'vue-select/dist/vue-select.css'
import '@adyen/adyen-web/styles/adyen.css'
import router from '@/app/Checkout/router'
import installVeeValidate from './plugins/veevalidate'
import DynamicLink from '@/components/modules/DynamicLink/index.vue'

configure({
  validateOnInput: true,
})

for (const [ruleName, ruleFn] of Object.entries(rules)) {
  if (typeof ruleFn === 'function') {
    defineRule(ruleName, ruleFn)
  }
}

installCustomRules(defineRule)

const rootEl = document.getElementById("checkout-purchase")

if (rootEl) {
  const app = createApp(Checkout)
  app.component('BaseModal', BaseModal)

  app.use( i18n )
  app.use( store )
  app.use( router )
  app.mixin( hasFeature )
  app.component('vue-select', VueSelect)
  app.directive('click-outside', clickOutside)
  app.provide("urls", {
    urlTerms: rootEl?.dataset.urlTerms,
    urlPrivacy: rootEl?.dataset.urlPrivacy,
    urlWithdraw: rootEl?.dataset.urlWithdrawl,
  })

  app.config.globalProperties.$ = $
  app.config.globalProperties.cleanSource = cleanSource
  app.config.globalProperties.toggleModal = toggleModal
  app.config.globalProperties.showSnackbar = showSnackbar
  app.config.globalProperties.snakeToPascal = snakeToPascal
  app.config.globalProperties.toggleBodyClass = toggleBodyClass
  app.config.globalProperties.toggleHtmlClass = toggleHtmlClass

  installVeeValidate(app)

  loadLanguageAsync().then(() => {
    exposeStores(store)
    app.mount('#checkout-purchase')
  })
}

// Quick-cart overlay is injected via AJAX when user clicks cart; mount when #quick-cart appears
function mountQuickCart() {
  const el = document.getElementById('quick-cart')
  if (!el || el.__vue_app__) return

  if (window.__quickCartApp) {
    try {
      window.__quickCartApp.unmount()
    } catch (e) {
      console.warn('QuickCart: could not be unmounted fully', e)
    }
    window.__quickCartApp = null
  }

  const app = createApp(QuickCart)
  app.component('BaseModal', BaseModal)
  app.use(i18n)
  app.use(store)
  app.use(createOverlayRouter())
  app.mixin(hasFeature)
  app.use(PerfectScrollbarPlugin)
  app.component('vue-select', VueSelect)
  app.directive('click-outside', clickOutside)
  app.config.globalProperties.cleanSource = cleanSource
  app.config.globalProperties.toggleModal = toggleModal
  app.config.globalProperties.showSnackbar = showSnackbar
  app.config.globalProperties.snakeToPascal = snakeToPascal
  app.config.globalProperties.toggleBodyClass = toggleBodyClass
  app.config.globalProperties.toggleHtmlClass = toggleHtmlClass

  // Mount immediately so API calls run; don't block on translations
  loadLanguageAsync().then(() => {}, () => {})
  exposeStores(store)
  app.mount(el)
  window.__quickCartApp = app
}

function tryMountQuickCart() {
  mountQuickCart()
}

if (document.readyState === 'loading') {
  document.addEventListener('DOMContentLoaded', tryMountQuickCart)
} else {
  tryMountQuickCart()
}
document.addEventListener('nb-overlay-loaded', (e) => {
  if (e.detail?.targetId === 'quick-cart') mountQuickCart()
})
document.addEventListener('quickcart:open', () => {
  if (window.store) window.store.dispatch('cart/openQuickCart')
})
document.addEventListener('quickcart:close', () => {
  if (window.store) window.store.dispatch('cart/closeQuickCart')
})

const quickCartObserver = new MutationObserver(() => {
  if (document.getElementById('quick-cart')) {
    mountQuickCart()
    quickCartObserver.disconnect()
  }
})
if (document.body) {
  quickCartObserver.observe(document.body, { childList: true, subtree: true })
}

// Obtain all auth-field elements
const elements = document.getElementsByTagName('nav-links')

if (elements.length) {
  Array.from(elements).forEach(el => {
    // Check if the element already has a Vue app instance if so unmount first
    if (el.__vue_app__) {
      try {
        el.__vue_app__.unmount()
      } catch (e) {
        console.warn('DynamicLink: could not be unmounted', e)
      }
    }

    const app = createApp(DynamicLink, { ...el.dataset })

    app.use(i18n)
    app.use(store)

    app.mount(el)
  })
}

function exposeStores(store) {
  if ( !window.store ) {
    window.store = store
  }
}
